/**
 * horizonOverlay — "Prognose vor N Tagen" reconstruction (#2341).
 *
 * A forecast entity (yield_forecast) is a temporal-vintage: identity is
 * (crop_cycle × forecast_date), and each re-issue is a REVISION whose
 * ``valid_from`` IS the forecast's own ``generated_at`` (the issue/decision
 * date). The overlay answers "what did we forecast for day D, as it stood N
 * days before D" — so for each target day D it must pick the vintage whose
 * ``generated_at`` is the latest one on or before ``D − N`` and MUST NOT smear
 * a later revision's value back onto an earlier horizon.
 *
 * The selection is keyed on the explicit ``generated_at`` attribute (surfaced
 * as each revision's ``valid_from`` in the entity series), never on transaction
 * time (``computed_at``/``superseded_at``), which a bulk backfill would collapse
 * onto the load day. Pure + unit-tested; the hook supplies the vintage pool.
 */

import { bucketLabel, type OutputForecastPoint } from './outputSeries';

/** One forecast revision: the value issued for ``forecastDate`` at ``generatedAt``. */
export interface ForecastVintage {
  forecastDate: string; // ISO ``YYYY-MM-DD`` — the target day (stable identity)
  generatedAt: string; // ISO instant — issue date (= the revision's valid_from)
  value: number;
}

/** Subtract ``days`` from an ISO date, in UTC, returning ``YYYY-MM-DD``. */
export function subtractDays(dateIso: string, days: number): string {
  const [y, m, d] = dateIso.slice(0, 10).split('-').map(Number);
  const ms = Date.UTC(y, m - 1, d) - days * 86_400_000;
  return new Date(ms).toISOString().slice(0, 10);
}

/**
 * The value of the vintage of ``forecastDate`` that was generated on or before
 * ``forecastDate − horizonDays`` (the latest such revision). Returns ``null``
 * when no vintage is that old — the caller then emits NO point for that day, so
 * the overlay never smears a fresher revision back over the horizon.
 */
export function selectVintageAtHorizon(
  vintages: ForecastVintage[],
  forecastDate: string,
  horizonDays: number,
): number | null {
  const cutoff = subtractDays(forecastDate, horizonDays);
  let best: ForecastVintage | null = null;
  for (const v of vintages) {
    if (v.forecastDate !== forecastDate) continue;
    if (v.generatedAt.slice(0, 10) > cutoff) continue; // generated too late for this horizon
    if (best === null || v.generatedAt > best.generatedAt) best = v;
  }
  return best ? best.value : null;
}

/**
 * Reconstruct the horizon-overlay forecast points: for each distinct target day
 * present in ``vintages``, the value as it stood ``horizonDays`` before that day.
 * Days with no old-enough vintage are omitted (no smear).
 */
export function buildHorizonForecastPoints(
  vintages: ForecastVintage[],
  horizonDays: number,
): OutputForecastPoint[] {
  const days = [...new Set(vintages.map((v) => v.forecastDate))].sort();
  const out: OutputForecastPoint[] = [];
  for (const forecastDate of days) {
    const value = selectVintageAtHorizon(vintages, forecastDate, horizonDays);
    if (value != null) out.push({ forecastDate, value });
  }
  return out;
}

// ── #2776 — horizon availability ────────────────────────────────────────────
// The selector above is honest by construction: no old-enough vintage ⇒ no
// point. What it cannot do is stop the UI from OFFERING a horizon the pool can
// never resolve — which is how WF prod (one Stand, 23-day max lead, choices
// 30/60/90) drew an empty overlay at every setting, with no line and no
// explanation. These helpers close that: derive the resolvable ladder steps
// from the pool's real lead times (#2419's pick-aware ``availableOffsets``
// pattern) and, when the overlay still comes up empty, say why.

/**
 * The URL-valid ``?h=`` universe. FIXED, not data-derived: ``?h=`` is a
 * deep-linkable param (see the ErnteprognosePage header contract), so its
 * accepted values must not drift with a tenant's data — a data-derived "23"
 * would make yesterday's link resolve to nothing tomorrow. #2776 extends the
 * original ``[30, 60, 90]`` DOWNWARD instead: 7 and 14 give a fresh, weekly
 * re-issued pool a working control on day one, while ``?h=45`` keeps resolving
 * to null (overlay off) exactly as before. Which steps are OFFERED is the
 * per-pool question ``availableHorizonChoices`` answers.
 */
export const HORIZON_LADDER: readonly number[] = [7, 14, 30, 60, 90];

/** UTC midnight of an ISO date/instant's DATE PART, or null if unparseable. */
function dayMs(dateIso: string): number | null {
  const [y, m, d] = dateIso.slice(0, 10).split('-').map(Number);
  const ms = Date.UTC(y, m - 1, d);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * How many whole days a forecast was issued AHEAD of the day it forecasts —
 * the lead time of one vintage. Date-part + UTC, the same comparison
 * ``selectVintageAtHorizon`` makes, so a lead of exactly N resolves a horizon
 * of exactly N (no off-by-one at the boundary). Negative when a vintage was
 * issued after its own target day; null when either side is unparseable.
 */
export function leadDays(forecastDate: string, generatedAt: string): number | null {
  const f = dayMs(forecastDate);
  const g = dayMs(generatedAt);
  if (f == null || g == null) return null;
  return Math.round((f - g) / 86_400_000);
}

/**
 * The longest lead in a pool — the deepest lookback it can possibly resolve.
 * Entries missing either field, and vintages issued at or after their target
 * day (lead ≤ 0 resolves no horizon on the ladder), contribute nothing; null
 * when nothing usable remains.
 *
 * Accepts anything carrying the two fields, so it reads BOTH the eager
 * current-state feed (``ForecastEntity`` — one latest ``generated_at`` per
 * forecast, hence a conservative LOWER bound on the true max lead) and the
 * lazily fetched revision pool (``ForecastVintage`` — exact).
 */
export function maxLeadDays(entries: { forecastDate: string | null; generatedAt: string | null }[]): number | null {
  let max: number | null = null;
  for (const e of entries) {
    if (e.forecastDate == null || e.generatedAt == null) continue;
    const lead = leadDays(e.forecastDate, e.generatedAt);
    if (lead == null || lead <= 0) continue;
    if (max == null || lead > max) max = lead;
  }
  return max;
}

/**
 * The ladder steps a pool with this max lead can actually resolve — i.e. the
 * horizons that yield at least one point. Sound by the selector's own rule: an
 * entity whose lead is ≥ N has a vintage generated on or before
 * ``forecastDate − N``, so ``selectVintageAtHorizon`` returns a value for that
 * day. Returns them ascending; a null/non-positive lead offers nothing.
 */
export function availableHorizonChoices(maxLead: number | null, ladder: readonly number[]): number[] {
  if (maxLead == null || maxLead <= 0) return [];
  return ladder.filter((n) => n <= maxLead).sort((a, b) => a - b);
}

/**
 * Why the requested lookback is empty, in plain German (#2601-B "say so" —
 * ``ohneSorteHintText`` / ``forecastCoverageHintText`` are the precedents). An
 * empty overlay must never be the only feedback, and a deep-linked ``?h=`` is
 * never silently rewritten, so the note carries the pool's CONCRETE facts:
 * earliest Stand and the deepest lookback it supports. ``hasShorterChoice``
 * adds the one action that would help.
 */
export function horizonEmptyNoteText(
  horizonDays: number,
  vintages: ForecastVintage[],
  hasShorterChoice: boolean,
): string {
  const head = `Kein Prognose-Rückblick für „vor ${horizonDays} Tagen“ möglich: `;
  const tail = hasShorterChoice ? ' Wähle einen kürzeren Zeitraum.' : '';
  const stands = [...new Set(vintages.map((v) => v.generatedAt.slice(0, 10)))].sort();
  if (stands.length === 0) return `${head}es liegen keine Prognose-Stände vor.${tail}`;
  const maxLead = maxLeadDays(vintages);
  // No usable lead at all (every Stand issued at/after its target day): name the
  // Stände, but do not print a "max. 0 Tage Vorlauf" that reads like a measurement.
  const lead = maxLead == null ? '' : ` (max. ${maxLead} ${maxLead === 1 ? 'Tag' : 'Tage'} Vorlauf)`;
  const stand =
    stands.length === 1
      ? `es liegt nur ein Prognose-Stand vom ${bucketLabel(stands[0], 'day')} vor`
      : `es liegen nur Prognose-Stände seit dem ${bucketLabel(stands[0], 'day')} vor`;
  return `${head}${stand}${lead}.${tail}`;
}
